import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { initAnalytics, trackPageview } from '@/lib/analytics';
import { startRouteTiming, finishRouteTiming } from '@/lib/route-timing';
import { isPrefetchedPath, isPathCached, markLoaded } from '@/utils/route-prefetch';

/**
 * Initializes analytics once and fires a pageview on every route change.
 * Render once inside <BrowserRouter>.
 */
export default function AnalyticsTracker(): null {
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    initAnalytics();
  }, []);

  // Begin timing as early as possible for the new path (before children
  // commit), so a Suspense fallback rendered during this commit is captured
  // inside the start->suspense window.
  useLayoutEffect(() => {
    const path = location.pathname + location.search;
    startRouteTiming(
      path,
      isPrefetchedPath(location.pathname),
      isPathCached(location.pathname),
    );
  }, [location.pathname, location.search]);

  useEffect(() => {
    const path = location.pathname + location.search;
    trackPageview(path);

    // Skip the very first commit's "finish" — there is no prior navigation
    // to consolidate. Subsequent renders mark render-complete on the next
    // animation frame so layout/paint work is included.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // Still emit a baseline timing for the initial load so dashboards
      // see a row for the first paint.
      const raf =
        typeof requestAnimationFrame !== 'undefined'
          ? requestAnimationFrame(() => {
              finishRouteTiming(path);
              markLoaded(path);
            })
          : (setTimeout(() => {
              finishRouteTiming(path);
              markLoaded(path);
            }, 1) as unknown as number);
      return () => {
        if (typeof cancelAnimationFrame !== 'undefined') {
          cancelAnimationFrame(raf);
        } else {
          clearTimeout(raf as unknown as ReturnType<typeof setTimeout>);
        }
      };
    }

    const raf =
      typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame(() => {
            finishRouteTiming(path);
            markLoaded(path);
          })
        : (setTimeout(() => {
            finishRouteTiming(path);
            markLoaded(path);
          }, 1) as unknown as number);
    return () => {
      if (typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(raf);
      } else {
        clearTimeout(raf as unknown as ReturnType<typeof setTimeout>);
      }
    };
  }, [location.pathname, location.search]);

  return null;
}
